import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAlert } from '../hooks/useAlert';
import Header from '../components/Header';
import './Billing.css';

function Billing() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { showError } = useAlert();
  const navigate = useNavigate();
  const [billingHistory, setBillingHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  // Redirect to auth if not authenticated
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBillingHistory();
    }
  }, [isAuthenticated]);

  const fetchBillingHistory = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Authentication required');
        setIsLoading(false);
        return;
      }

      const response = await fetch('/api/billing/history', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBillingHistory(data.invoices || []);
      } else {
        setError('Failed to load billing history');
      }
    } catch (err) {
      console.error('Error fetching billing history:', err);
      setError('Unable to load billing history');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const handleOpenBillingPortal = async () => {
    setIsOpeningPortal(true);
    try {
      const response = await fetch('/api/stripe/create-portal-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open billing portal');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error('Error opening billing portal:', err);
      await showError('Billing Portal Error', err.message || 'Failed to open billing portal');
      setIsOpeningPortal(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      paid: 'status-paid',
      open: 'status-open',
      void: 'status-void',
      uncollectible: 'status-uncollectible',
      draft: 'status-draft'
    };

    return (
      <span className={`status-badge ${statusClasses[status] || ''}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="billing-page">
        <Header />
        <main className="billing-main">
          <h1>Billing History</h1>
          <div className="loading-state">Loading billing history...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <Header />
      <main className="billing-main">
        <h1>Billing History</h1>
        <div className="billing-info">
          <div className="current-plan-info">
            <h2>Current Plan</h2>
            <p className="plan-name">
              {user?.tier === 'basic' ? 'Basic Plan - $2/month' :
               user?.tier === 'premium' ? 'Premium Plan - $8/month' : 'Free Plan'}
            </p>
            <div className="billing-actions">
              <Link to="/plans-and-payment" className="manage-plan-link">
                Manage Plan
              </Link>
              {user?.tier !== 'free' && (
                <button
                  onClick={handleOpenBillingPortal}
                  className="billing-portal-btn"
                  disabled={isOpeningPortal}
                >
                  {isOpeningPortal ? 'Opening...' : 'Stripe Billing Portal'}
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {!error && billingHistory.length === 0 && (
          <div className="empty-state">
            <p>No billing history available</p>
            {user?.tier === 'free' && (
              <Link to="/plans-and-payment" className="upgrade-link">
                Upgrade to a paid plan
              </Link>
            )}
          </div>
        )}

        {billingHistory.length > 0 && (
          <div className="billing-table-container">
            <div className="billing-history-header">
              <h2>Invoice History</h2>
              <button
                onClick={handleOpenBillingPortal}
                className="stripe-portal-link-btn"
                disabled={isOpeningPortal}
                title="View complete billing history and manage payment methods in Stripe"
              >
                {isOpeningPortal ? 'Opening...' : 'View in Stripe →'}
              </button>
            </div>
            <table className="billing-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {billingHistory.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{formatDate(invoice.created)}</td>
                    <td>
                      {invoice.lines?.data?.[0]?.description || 'Subscription'}
                      {invoice.period_start && invoice.period_end && (
                        <div className="billing-period">
                          {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                        </div>
                      )}
                    </td>
                    <td className="amount">{formatAmount(invoice.amount_paid || invoice.total)}</td>
                    <td>{getStatusBadge(invoice.status)}</td>
                    <td>
                      {invoice.invoice_pdf && (
                        <a
                          href={invoice.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="download-link"
                        >
                          Download
                        </a>
                      )}
                      {invoice.hosted_invoice_url && !invoice.invoice_pdf && (
                        <a
                          href={invoice.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-link"
                        >
                          View
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {user?.tier !== 'free' && (
          <div className="billing-notes">
            <h3>Automatic Invoicing</h3>
            <p>
              Your subscription is billed automatically each month. Invoices are sent to your email
              and are available here for download. You can update your payment method or cancel your
              subscription at any time from the{' '}
              <Link to="/plans-and-payment">Plans & Payment</Link> page.
            </p>
            <p className="stripe-portal-note">
              For complete billing history and detailed invoice management, visit the{' '}
              <button
                onClick={handleOpenBillingPortal}
                className="inline-stripe-link"
                disabled={isOpeningPortal}
              >
                Stripe Customer Portal
              </button>.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

export default Billing;